import { Alert, FormLabel, TextField } from '@cmsgov/ds-medicare-gov';
import { ChangeEventHandler, MouseEventHandler, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import LastNameSection from '../../common/components/LastNameSection';
import MedicareNumberSection from '../../common/components/MedicareNumberSection';
import Button from '../../common/components/button';
import Div from '../../common/components/div';
import H1 from '../../common/components/h1';
import StackingButtons from '../../common/components/StackingButtons';
import StepHeader from '../../common/components/StepHeader';
import TranslatedSpan from '../../common/components/TranslatedSpan';
import capitalizeFirstLetter from '../../common/functions/capitalizeFirstLetter';
import capitalizeWords from '../../common/functions/capitalizeWords';
import { isValidMedicareNumber } from '../../common/functions/isValidMedicareNumber';
import { isValidYear } from '../../common/functions/isValidYear';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart, usePageViewWithSteps } from '../../common/hooks/use.analytics';
import {ForgotPasswordErrorsResponse, useForgotPasswordApi} from '../../common/hooks/useForgotPasswordApi';
import { usePageTitle } from '../../common/hooks/usePageTitle';
import { DateObject } from '../../common/objects/models/date.field.value';
import { NewPassword } from '../../common/components/NewPassword/NewPassword';
import { PasswordErrState } from '../../common/components/NewPassword/passwordSchema';
import { AllFieldsRequired } from '../../common/components/AllFieldsRequired';
import { ForgotPassword1Fields, ForgotPassword3Fields, SmartErrorAlert } from '../../common/components/SmartErrorAlert';
import { InvalidField } from '../../common/functions/errorAlertContent/getAlertContent';
import { DateFields2 } from '../../common/components/DateFields2';

interface StepProps {
    title?: string
    stepNum: number
    stepTitle?: string
    combinedWithUsername?: boolean
    allFieldsRequired?: boolean
}

const Step = (props: PropsWithChildren<StepProps>) => {
    const {title, stepNum, stepTitle, combinedWithUsername, allFieldsRequired} = props;
    const step = combinedWithUsername ? stepNum + 1 : stepNum;
    const numSteps = combinedWithUsername ? 4 : 3;
    const pageTitle = combinedWithUsername ? translate('forgot_username_and_password') : title;
    const showStepNum = step && step <= numSteps;

    const [headingRef, setHeadingRef] = useState<HTMLHeadingElement | null>(null);

    usePageTitle(pageTitle || capitalizeFirstLetter(translate('reset_password').toLowerCase()));
    usePageViewWithSteps(pageTitle || capitalizeFirstLetter(translate('reset_password').toLowerCase()), step, stepTitle || title || '');

    // When refactoring the reset password flow into multiple components this should no longer be necessary
    useEffect(() => {
        setTimeout(() => {
            headingRef?.focus();
        }, 10);
    }, [stepNum, headingRef]);
    const header = pageTitle || translate('reset_password');
    return <>
        <Div
            id="step"
            isContainer={true}
            paddingySM={1} paddingyMD={2} paddingyLG={3} paddingyXL={4}
        >
            <Div isRow>
                <Div cols={12}>
                    <H1 headingRef={setHeadingRef} className='ds-u-padding-bottom--3'>{pageTitle || capitalizeFirstLetter(translate('reset_password').toLowerCase())}</H1>
                    {combinedWithUsername &&
               <div className='ds-u-padding-bottom--3'>
                   <TranslatedSpan english={'get_username_and_reset_password'}/>
               </div>
                    }
                    {showStepNum &&
               <legend>
                   <Div paddingBottom={1}>
                       <StepHeader
                           currentStep={step}
                           numSteps={numSteps}
                           stepTitle={stepTitle || ''}
                       />
                   </Div>
               </legend>
                    }
                    {allFieldsRequired &&
                        <Div marginTop={3}>
                            <AllFieldsRequired />
                        </Div>
                    }
                </Div>
            </Div>
            {props.children}
        </Div>
    </>
};

interface ButtonProps {
    backText: string
    forwardText: string
    onBackClick: MouseEventHandler
    onForwardClick: MouseEventHandler
    isLoading: boolean
    testId?: string
}

const Buttons = (props: ButtonProps) => {
    const {backText, forwardText, onBackClick, onForwardClick, isLoading} = props;

    const getBackButton = () => {
        return <>
            <Button
                onClick={onBackClick}
            >
                {backText}
            </Button>
        </>;
    };

    const getForwardButton = () => {
        return <>
            <Button
                onClick={onForwardClick}
                mfaAnalyticsData={{
                    text: forwardText,
                    parentComponentHeading: translate('reset_password')
                }}
                variation={'solid'}
                isLoading={isLoading}
            >
                {forwardText}
            </Button>
        </>;
    };

    return <>
        <StackingButtons
            button1={getBackButton()}
            button2={getForwardButton()}
        />
    </>;
};

interface ForgotPasswordProps {
    combinedWithUsername: boolean
    secretQuestion: string
    onBackClick: MouseEventHandler
    forgotUsernameData?: {
        dateOfBirth: DateObject
        lastName: string
        medicareNumber: string
        suffix: string
        username: string
    }
    testStep?: number | null
}

type FieldErrState = 'empty' | 'invalid' | null;

const ForgotPassword = (props: ForgotPasswordProps) => {
    const startingStep = props.combinedWithUsername ? 1 : 0;
    const question = props.secretQuestion ?? '';
    const forgotPasswordApi = useForgotPasswordApi();
    const [step, setStep] = useState(props.testStep ? props.testStep : startingStep);
    const [username, setUsername] = useState('');
    const [usernameRef, setUsernameRef] = useState<HTMLElement | null>(null);
    const [medicareNumber, setMedicareNumber] = useState('');
    const [medicareNumRef, setMedicareNumRef] = useState<HTMLElement | null>(null);
    const [lastName, setLastName] = useState('');
    const [lastNameRef, setLastNameRef] = useState<HTMLElement | null>(null);
    const [suffix, setSuffix] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState({day: '', month: '', year: ''});
    const [monthFieldRef, setMonthFieldRef] = useState<HTMLElement | null>(null);
    const [enterInfoLoading, setEnterInfoLoading] = useState(false);
    const [secretQuestion, setSecretQuestion] = useState(question);
    const [answerQuestionLoading, setAnswerQuestionLoading] = useState(false);
    const [secretAnswer, setSecretAnswer] = useState('');
    const [password, setPassword] = useState('');
    const [passwordRef, setPasswordRef] = useState<HTMLElement | null>(null);
    const [confirmPasswordRef, setConfirmPasswordRef] = useState<HTMLElement | null>(null);
    const [submitPasswordLoading, setSubmitPasswordLoading] = useState(false);
    const [answerFailCount, setAnswerFailCount] = useState(0);

    const [passwordErrs, setPasswordErrs] = useState<Set<PasswordErrState>>(new Set());

    const isFirstStep = step === 0;
    const formStart = useFormStart('reset_password', isFirstStep);
    const analyticsComponentName = props.combinedWithUsername ? 'forgot username and password' : 'reset_password';
    const conversion = useConversion(analyticsComponentName, 'reset_password');

    const [step1ApiErrResponse, setStep1ApiErrResponse] = useState<ForgotPasswordErrorsResponse | null>(null);
    const [step2ApiErrResponse, setStep2ApiErrResponse] = useState<ForgotPasswordErrorsResponse | null>(null);
    const [step3ApiErrResponse, setStep3ApiErrResponse] = useState<ForgotPasswordErrorsResponse | null>(null);

    const [step1InvalidFields, setStep1InvalidFields] = useState<InvalidField<ForgotPassword1Fields>[]>([]);
    const [step3InvalidFields, setStep3InvalidFields] = useState<InvalidField<ForgotPassword3Fields>[]>([]);

    const [submit1Attempted, setSubmit1Attempted] = useState(false);
    const [submit2Attempted, setSubmit2Attempted] = useState(false);
    const [submit3Attempted, setSubmit3Attempted] = useState(false);

    const [mbiTouched, setMbiTouched] = useState(false);
    const [usernameTouched, setUsernameTouched] = useState(false);
    const [lastNameTouched, setLastNameTouched] = useState(false);
    const [dateOfBirthTouched, setDateOfBirthTouched] = useState(false);
    const [secretAnswerTouched, setSecretAnswerTouched] = useState(false);

    const [mbiErrState, setMbiErrState] = useState<FieldErrState>(null);
    const [usernameErrState, setUsernameErrState] = useState<FieldErrState>(null);
    const [lastNameErrState, setLastNameErrState] = useState<FieldErrState>(null);
    const [dateOfBirthErrState, setDateOfBirthErrState] = useState<FieldErrState>(null);
    const [secretAnswerErrState, setSecretAnswerErrState] = useState<FieldErrState>(null);
    const [passwordErrState, setPasswordErrState] = useState<FieldErrState | 'alreadyUsed'>(null);
    const [confirmPasswordErrState, setConfirmPasswordErrState] = useState<FieldErrState>(null);

    useEffect(() => {
        const isValid = isValidMedicareNumber(medicareNumber);
        const isEmpty = !medicareNumber;
        if (isValid) {
            setMbiErrState(null);
        } else if (isEmpty) {
            setMbiErrState('empty');
        } else {
            setMbiErrState('invalid');
        }
    }, [medicareNumber]);

    const getMbiErrMessage = () => {
        if (!mbiTouched && !submit1Attempted) {
            return null;
        }
        if (mbiErrState === 'empty') {
            return translate('enter_your_medicare_number');
        }
        if (mbiErrState === 'invalid') {
            return translate('check_mbi_and_try_again')
        }
        return null;
    };

    useEffect(() => {
        setUsernameErrState(username.length ? null : 'empty');
    }, [username]);

    const getUsernameErrMessage = () => {
        if (!usernameTouched && !submit1Attempted) {
            return null;
        }
        return usernameErrState ? translate('enter_your_usermame_with_period') : null;
    };

    useEffect(() => {
        setLastNameErrState(lastName.length ? null : 'empty');
    }, [lastName]);

    const getLastNameErrMessage = () => {
        if (!lastNameTouched && !submit1Attempted) {
            return null;
        }
        return lastNameErrState ? translate('enter_your_last_name') : null;
    }

    useEffect(() => {
        const { month, day, year } = dateOfBirth;
        const isEmpty = !month && !day && !year;
        const isInvalid = !isValidYear(year);

        if (isEmpty) {
            setDateOfBirthErrState('empty');
        } else if (isInvalid) {
            setDateOfBirthErrState('invalid');
        } else {
            setDateOfBirthErrState(null);
        }
    }, [dateOfBirth]);

    const getDateOfBirthErrMessage = () => {
        if (!dateOfBirthTouched && !submit1Attempted) {
            return null;
        }
        if (dateOfBirthErrState === 'empty') {
            return translate('enter_your_dob');
        }
        if (dateOfBirthErrState === 'invalid') {
            return translate('check_dob_try_again');
        }
        return null;
    };

    useEffect(() => {
        setSecretAnswerErrState(secretAnswer.length ? null : 'empty');
    }, [secretAnswer]);

    const getSecretAnswerErrMessage = () => {
        if (!secretAnswerTouched && !submit2Attempted) {
            return null;
        }
        return secretAnswerErrState ? translate('enter_answer_to_your_secret_question') : null;
    }

    /**
     * The NewPassword component handles its own errors, so we will set the passwordErrState
     * primarily based on the error state coming from the the component and not the value of
     * the password itself
     */
    const lastPassword = useRef('');
    const lastNumErrs = useRef(-1);
    useEffect(() => {
        // only run when password or number of errors changes, to prevent prematurely overriding
        // state changes set in onStep3InvalidFields()
        if (passwordErrs.size === lastNumErrs.current && password === lastPassword.current) {
            return;
        }
        lastNumErrs.current = passwordErrs.size;
        lastPassword.current = password;

        const errState: FieldErrState = password.length ? 'invalid' : 'empty';
        setPasswordErrState(passwordErrs.size ? errState : null);
    }, [passwordErrs, password]);

    useEffect(() => {
        if (props.forgotUsernameData) {
            setDateOfBirth(props.forgotUsernameData.dateOfBirth);
            setLastName(props.forgotUsernameData.lastName);
            setMedicareNumber(props.forgotUsernameData.medicareNumber);
            setSuffix(props.forgotUsernameData.suffix);
            setUsername(props.forgotUsernameData.username);
        }
    }, [props.forgotUsernameData]);

    const history = useHistory();
    const goToLogin = () => history.push('/login');

    const onUsernameChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        formStart();
        const newName = event.target.value.trim();
        setUsername(newName);
    };

    const onMedicareNumberChange = (value: string) => {
        formStart();
        const newNum = value.trim();
        setMedicareNumber(newNum);
    };

    const onLastNameChange = (name: string) => {
        formStart();
        name = name.trim();
        setLastName(name);
    };

    const onSuffixChange = (suffixVal: string) => {
        formStart();
        setSuffix(suffixVal);
    };

    const onDateOfBirthChage = (dob: DateObject) => {
        formStart();
        setDateOfBirth(dob);
    };

    const onSecretAnswerChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        formStart();
        const newAns = event.target.value;
        setSecretAnswer(newAns);
    };

    const onPasswordChange = (newPassword: string) => {
        formStart();
        setPassword(newPassword);
    };

    const onConfirmPasswordChange = () => {
        formStart();
    };

    const onStep1InvalidFields = (fields: InvalidField<ForgotPassword1Fields>[]) => {
        fields.forEach(f => {
            if (f.name === 'mbi') {
                setMbiErrState('invalid');
            }
            if (f.name === 'username') {
                setUsernameErrState('invalid');
            }
            if (f.name === 'lastName') {
                setLastNameErrState('invalid');
            }
            if (f.name === 'dateOfBirth') {
                setDateOfBirthErrState('invalid');
            }
        });
    }

    const onStep3InvalidFields = (fields: InvalidField<ForgotPassword3Fields>[]) => {
        fields.forEach(field => {
            if (field.name === 'newPassword') {
                setPasswordErrState(field.type);
            }
        });
    }

    const prev = () => {
        setStep(i => i - 1);
    };

    const next = () => {
        setStep(i => i + 1);
    };

    const getSecretQuestion = async () => {
        return forgotPasswordApi.getSecretQuestion({
            mbi: medicareNumber,
            username,
            lastName,
            suffix,
            dateOfBirth
        });
    };

    const submitSecretAnswer = () => {
        return forgotPasswordApi.answerSecretQuestion(medicareNumber, secretQuestion, secretAnswer);
    };

    const submitNewPassword = async () => {
        return forgotPasswordApi.resetPassword({
            mbi: medicareNumber,
            username,
            lastName,
            suffix,
            dateOfBirth,
            secretQuestion,
            secretAnswer,
            password
        });
    };

    const enterInformationSubmit = async () => {
        setSubmit1Attempted(true);
        if (enterInfoLoading) {
            return;
        }
        conversion.attempt();

        const newInvalidFields: InvalidField<ForgotPassword1Fields>[] = [];
        if (usernameErrState) {
            newInvalidFields.push({ name: 'username', type: usernameErrState });
        }
        if (mbiErrState) {
            newInvalidFields.push({ name: 'mbi', type: mbiErrState });
        }
        if (lastNameErrState) {
            newInvalidFields.push({ name: 'lastName', type: lastNameErrState });
        }
        if (dateOfBirthErrState) {
            newInvalidFields.push({ name: 'dateOfBirth', type: dateOfBirthErrState });
        }
        setStep1InvalidFields(newInvalidFields);
        if (newInvalidFields.length) {
            return;
        }

        setEnterInfoLoading(true);
        try {
            setStep1ApiErrResponse(null);
            const question = await getSecretQuestion();
            setSecretQuestion(question);
            next();
        } catch (e: any) {
            setStep1ApiErrResponse(e);
        } finally {
            setEnterInfoLoading(false);
        }
    };

    const secretQuestionSubmit = async () => {
        setSubmit2Attempted(true);
        conversion.attempt();
        if (secretAnswerErrState) {
            // manually trigger conversion failure because this step doesn't show an error alert for validation errors
            conversion.failValidationIntermediate('Enter the answer to your secret question');
            return;
        }

        try {
            setStep2ApiErrResponse(null);
            setAnswerQuestionLoading(true);
            await submitSecretAnswer();
            next();
        } catch (e: any) {
            setStep2ApiErrResponse(e);
            if (e.code === 403) {
                setAnswerFailCount(lastCount => lastCount + 1);
            }
        } finally {
            setAnswerQuestionLoading(false);
        }
    };

    const resetPassword = async () => {

        setSubmit3Attempted(true);
        if (submitPasswordLoading) {
            return;
        }
        conversion.attempt();

        const newInvalidFields: InvalidField<ForgotPassword3Fields>[] = [];
        if (passwordErrState) {
            newInvalidFields.push({ name: 'newPassword', type: passwordErrState });
        }
        if (confirmPasswordErrState) {
            newInvalidFields.push({ name: 'confirmPassword', type: confirmPasswordErrState });
        }
        setStep3InvalidFields(newInvalidFields);

        if (newInvalidFields.length) {
            return;
        }

        setSubmitPasswordLoading(true);
        try {
            setStep3ApiErrResponse(null);
            await submitNewPassword();
            conversion.success();
            next();
        } catch (e: any) {
            setStep3ApiErrResponse(e);
        } finally {
            setSubmitPasswordLoading(false);
        }

    };
    const infoHeader = translate('enter_your_information')
    const enterYourInfo = <Step stepNum={1} stepTitle={capitalizeWords(translate('enter_your_information'))} allFieldsRequired>
        <SmartErrorAlert
            name="ForgotPassword1"
            usernameRef={usernameRef}
            mbiRef={medicareNumRef}
            lastNameRef={lastNameRef}
            dateOfBirthRef={monthFieldRef}
            conversion={conversion}
            apiResponse={step1ApiErrResponse}
            invalidFields={step1InvalidFields}
            onInvalidField={onStep1InvalidFields}
            parentHeading={infoHeader}
        />
        <TextField
            errorMessage={getUsernameErrMessage()}
            inputRef={setUsernameRef}
            label={translate('username')}
            name={'username'}
            onBlur={() => setUsernameTouched(true)}
            onChange={onUsernameChange}
            onClick={formStart}
            value={username}
        />
        <MedicareNumberSection
            errorMessage={getMbiErrMessage()}
            inputRef={setMedicareNumRef}
            onBlur={() => setMbiTouched(true)}
            onChange={onMedicareNumberChange}
            value={medicareNumber}
            parentComponentHeading={infoHeader}
        />
        <LastNameSection
            errorMessage={getLastNameErrMessage()}
            inputRef={setLastNameRef}
            lastNameVal={lastName}
            onLastNameBlur={() => setLastNameTouched(true)}
            onLastNameChange={onLastNameChange}
            onSuffixChange={onSuffixChange}
            suffixVal={suffix}
        />
        <DateFields2
            date={dateOfBirth}
            hint={translate('use_format_mmddyyyy')}
            label={translate('date_of_birth')}
            onChange={onDateOfBirthChage}
            onComponentBlur={() => setDateOfBirthTouched(true)}
            monthFieldRef={setMonthFieldRef}
            errorMessage={getDateOfBirthErrMessage()}
        />
        <Buttons
            backText={translate('cancel')}
            forwardText={translate('next')}
            isLoading={enterInfoLoading}
            onBackClick={goToLogin}
            onForwardClick={enterInformationSubmit}
        />
    </Step>;

    const answerHeader = translate('answer_secret_question');
    const answerSecretQuestion = <Step stepNum={2} stepTitle={translate('answer_secret_question')} combinedWithUsername={props.combinedWithUsername} allFieldsRequired>
        <SmartErrorAlert
            name="ForgotPassword2"
            conversion={conversion}
            apiResponse={step2ApiErrResponse}
            incorrectAnswerCount={answerFailCount}
            parentHeading={answerHeader}
            // No alert for empty fields, so these props are unused
            secretAnswerRef={null}
            invalidFields={null}
            onInvalidField={() => {}}
        />
        <FormLabel>
            <TranslatedSpan english={'secret_question'}/>
        </FormLabel>

        <Div>{secretQuestion}</Div>
        <TextField
            errorMessage={getSecretAnswerErrMessage()}
            label={translate('secret_answer')}
            name={'secretAnswer'}
            onBlur={() => setSecretAnswerTouched(true)}
            onChange={onSecretAnswerChange}
            onClick={formStart}
            value={secretAnswer}
        />
        <Buttons
            backText={translate('back')}
            forwardText={translate('next')}
            isLoading={answerQuestionLoading}
            onBackClick={props.onBackClick ? props.onBackClick : prev}
            onForwardClick={secretQuestionSubmit}
        />
    </Step>;

    const passwordHeader = translate('create_new_password');
    const createNewPassword = <Step stepNum={3} stepTitle={translate('create_new_password')} combinedWithUsername={props.combinedWithUsername} allFieldsRequired>
        <SmartErrorAlert
            name="ForgotPassword3"
            conversion={conversion}
            apiResponse={step3ApiErrResponse}
            newPasswordRef={passwordRef}
            confirmPasswordRef={confirmPasswordRef}
            invalidFields={step3InvalidFields}
            onInvalidField={onStep3InvalidFields}
            parentHeading={passwordHeader}
        />
        <NewPassword
            component={'forgotPassword'}
            onPasswordChange={onPasswordChange}
            onConfirmPasswordChange={onConfirmPasswordChange}
            onPasswordErrorTypes={setPasswordErrs}
            onConfirmPasswordError={hasErr => setConfirmPasswordErrState(hasErr ? 'invalid' : null)}
            passwordLabel={translate('enter_new_password')}
            confirmPasswordLabel={translate('confirm_new_password')}
            passwordRef={setPasswordRef}
            confirmPasswordRef={setConfirmPasswordRef}
            submitAttempted={submit3Attempted}
            apiErrorKey={passwordErrState === 'invalid' ? 'passwordGuidelines' : undefined}
            showAlreadyUsedError={passwordErrState === 'alreadyUsed'}
        />
        <Buttons
            backText={translate('back')}
            forwardText={translate('reset_password')}
            isLoading={submitPasswordLoading}
            onBackClick={prev}
            onForwardClick={resetPassword}
        />
    </Step>;

    const successHeader = translate('your_password_reset');
    const success = <Step stepNum={4} title={translate('your_password_reset')} combinedWithUsername={props.combinedWithUsername}>
        <Div isRow>
            <Div cols={12} colsMD={8} marginBottom={3}>
                <Alert variation="success" hideIcon={true}>
                    <TranslatedSpan english={'you_all_set_password_changed'}/>
                </Alert>
            </Div>
        </Div>
        <Div
            isRow
            paddingBottom={3}
            style={{paddingLeft: 16}}>
            <Button variation="solid" mfaAnalyticsData={{
                text: translate('return_to_log_in'),
                parentComponentHeading: successHeader
            }} onClick={goToLogin}>
                <TranslatedSpan english={'return_to_log_in'}/>
            </Button>
        </Div>
    </Step>;

    const steps = [enterYourInfo, answerSecretQuestion, createNewPassword, success];

    return <>
        <fieldset style={{
            border: 0,
            margin: 0,
            padding: 0,
        }}>
            {steps[step]}
        </fieldset>
    </>;
};

export default ForgotPassword;
